/**
 * What a plan card's call to action does, as a pure function of billing
 * state and the viewer. One rule set for the homepage section, /pricing and
 * any future surface, unit tested in `pricing-cta.test.ts`.
 *
 *   start_free  Free plan: go learn (Course 1).
 *   waitlist    Billing not live (or unknown yet): founding-member capture.
 *   signup      Live, signed out: sign up, then resume checkout (intent).
 *   checkout    Live, signed in, no own subscription: Stripe Checkout.
 *   manage      Live, this is the viewer's own current plan: Customer Portal.
 *   switch      Live, viewer pays for the other paid plan: change it in the
 *               Customer Portal (prorated by Stripe).
 *   included    Viewer is Pro through a Team seat: nothing to buy.
 */
import type { PlanId } from './pricing'

/**
 * Fragment that scrolls /pricing to the founding-member form (its element id
 * is `FOUNDING_ANCHOR_ID` in `components/pricing/founding-waitlist.tsx`). One
 * constant for every "join the founding list" link.
 */
export const FOUNDING_HASH = '#founding'

export type PlanCtaAction =
  | 'start_free'
  | 'waitlist'
  | 'signup'
  | 'checkout'
  | 'manage'
  | 'switch'
  | 'included'

export type PlanCtaViewer = {
  signedIn: boolean
  plan: PlanId
  source: 'none' | 'admin' | 'subscription' | 'grant' | 'team_seat'
}

export function resolvePlanCta(input: {
  planId: PlanId
  /** `billing.status.live`; `null` while unknown (treated as not live). */
  live: boolean | null
  viewer: PlanCtaViewer
}): PlanCtaAction {
  const { planId, live, viewer } = input
  if (planId === 'free') return 'start_free'
  if (live !== true) return 'waitlist'
  if (!viewer.signedIn) return 'signup'
  if (viewer.source === 'subscription') {
    return viewer.plan === planId ? 'manage' : 'switch'
  }
  if (viewer.source === 'team_seat') return 'included'
  // Free, comped (grant) or admin: buying is allowed.
  return 'checkout'
}
